// src/pages/Home.jsx
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { saveLead } from "../services/lead.service";
import SeoManager from "../components/SEO/SeoManager";

const INITIAL_FORM = {
  name: "",
  company: "",
  contact: "",
  email: "",
  message: "",
  accepted: false,
};

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();

  // Context (si tu AuthContext tiene authLoading, lo usamos; si no, no rompe)
  const { user, authLoading, loading: ctxLoading } = useContext(AuthContext) ?? {};
  const isAuthLoading = Boolean(authLoading ?? ctxLoading);

  // --- Hooks SIEMPRE arriba (evita error rules-of-hooks)
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [serverSuccess, setServerSuccess] = useState("");
  const [formData, setFormData] = useState(INITIAL_FORM);

  const formRef = useRef(null);

  const canSubmit = useMemo(() => {
    return Boolean(
      !loading &&
        formData.name.trim() &&
        formData.contact.trim() &&
        formData.email.trim() &&
        formData.accepted
    );
  }, [formData, loading]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const scrollToForm = useCallback(() => {
    // Si tienes header fijo, puedes ajustar con scroll-margin-top en el contenedor
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setServerError("");
      setServerSuccess("");

      if (!formData.accepted) {
        setServerError("⚠️ Debes aceptar el tratamiento de datos personales.");
        return;
      }

      const payload = {
        fullName: formData.name.trim(),
        companyName: formData.company.trim(),
        phone: formData.contact.trim(),
        email: formData.email.trim().toLowerCase(),
        message: formData.message.trim(),
        acceptsPersonalData: formData.accepted,
        userCreated: "WEB_PUBLIC",
      };

      try {
        setLoading(true);
        const result = await saveLead(payload);

        if (result?.success === 1) {
          setServerSuccess(
            result?.message || "✅ Datos recibidos. Nos contactaremos pronto."
          );
          setFormData(INITIAL_FORM);
          return;
        }

        setServerError(result?.message || "❌ Error al enviar. Intenta de nuevo.");
      } catch (err) {
        setServerError("❌ Error de conexión con el servidor.");
      } finally {
        setLoading(false);
      }
    },
    [formData]
  );

  // Redirect si ya hay usuario
  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // ✅ Scroll automático al formulario cuando llegas desde otra página
  // Soporta:
  // - Link con state={{ scrollToForm: true }}
  // - URL con hash: /#form
  useEffect(() => {
    if (isAuthLoading || user) return;

    const wantsScrollFromState = Boolean(location?.state?.scrollToForm);
    const wantsScrollFromHash = window.location.hash === "#form";

    if (wantsScrollFromState || wantsScrollFromHash) {
      setTimeout(() => {
        document.getElementById("form")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        // Limpia el state para que no se repita al volver atrás/adelante
        if (wantsScrollFromState) {
          navigate(location.pathname, { replace: true, state: null });
        }
      }, 120);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading, user, location?.state, navigate, location?.pathname]);

  // Early returns (DESPUÉS de hooks)
  if (isAuthLoading) return null;
  if (user) return null;

  const title = "Software de Procesos Operativos para Campo y Planta | PRODSYNC";
  const description =
    "PRODSYNC digitaliza procesos operativos en campo y planta con trazabilidad, captura de evidencias, control de tiempos y ejecucion guiada desde app movil.";
  const canonicalUrl = "https://prodsyncapp.com/";
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "PRODSYNC",
        url: canonicalUrl,
        logo: "https://prodsyncapp.com/app_icon.png",
      },
      {
        "@type": "SoftwareApplication",
        name: "PRODSYNC",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, Android",
        url: canonicalUrl,
        description,
      },
    ],
  };

  return (
    <div className="font-sans antialiased text-slate-900 bg-white scroll-smooth">
      <SeoManager
        title={title}
        description={description}
        canonical={canonicalUrl}
        jsonLd={jsonLd}
      />
      {/* --- HERO SECTION --- */}
      <section className="relative bg-primaryDark py-16 lg:py-24 overflow-hidden">
        {/* Glow suave (como el estilo premium del screenshot) */}
        <div className="absolute top-0 left-0 w-full h-full opacity-15 pointer-events-none">
          <div className="absolute -top-10 left-1/4 w-96 h-96 bg-primaryBlue rounded-full blur-[140px]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Texto Hero */}
            <h1 className="sr-only">
              PRODSYNC - Software de Gestión de Procesos Operativos para Control
              de Actividades en Campo, Toma de Tiempos y Evidencias
            </h1>

            <div className="text-center lg:text-left space-y-8">
              <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-primaryBlue/10 text-primaryBlue border border-primaryBlue/20">
                Solución para Manufactura, Logística y Servicios
              </span>

              <p className="text-4xl md:text-6xl font-extrabold text-white leading-tight">
                Toma el control real de tu{" "}
                <span className="text-primaryBlue">operación en campo.</span>
              </p>

              <p className="text-mutedText text-lg md:text-xl max-w-xl mx-auto lg:mx-0">
                Elimina el papel y los procesos manuales. Captura evidencias,
                mide tiempos y garantiza el cumplimiento de estándares en tiempo
                real.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link
                  to="/control-operativo-en-campo"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-primaryBlue/40 bg-primaryBlue/10 px-5 py-3 font-bold text-primaryBlue transition-all hover:border-primaryBlue hover:bg-primaryBlue/20"
                >
                  Soluciones para control operativo en campo
                  <span aria-hidden="true">&rarr;</span>
                </Link>
                <Link
                  to="/software-para-manufactura"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 font-bold text-white transition-all hover:border-primaryBlue/50 hover:bg-white/10 hover:text-primaryBlue"
                >
                  Soluciones para manufactura
                  <span aria-hidden="true">&rarr;</span>
                </Link>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                <button
                  onClick={scrollToForm}
                  className="px-8 py-4 bg-primaryBlue hover:bg-primaryBlueHover text-white rounded-xl font-bold transition-all shadow-lg shadow-primaryBlue/30"
                >
                  Agendar Demo
                </button>

                <a
                  href="#ciclo"
                  className="px-8 py-4 bg-secondaryDark hover:brightness-110 text-white rounded-xl font-bold transition-all"
                >
                  Ver metodología
                </a>
              </div>
            </div>

            {/* Formulario Hero */}
            <div
              id="form"
              ref={formRef}
              className="bg-cardBg rounded-3xl shadow-2xl p-8 border border-slate-100 scroll-mt-6"
            >
              <div className="mb-6 text-center">
                <h3 className="text-2xl font-bold text-slate-900">
                  Solicitar Información
                </h3>
                <p className="text-slate-500">Optimiza tus recursos hoy mismo</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {serverError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {serverError}
                  </div>
                )}
                {serverSuccess && (
                  <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {serverSuccess}
                  </div>
                )}

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Nombre completo"
                  required
                  autoComplete="name"
                  className="w-full bg-inputBg border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primaryBlue outline-none"
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Empresa"
                    autoComplete="organization"
                    className="w-full bg-inputBg border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primaryBlue outline-none"
                  />

                  <input
                    type="tel"
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                    placeholder="Teléfono de contacto"
                    required
                    autoComplete="tel"
                    className="w-full bg-inputBg border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primaryBlue outline-none"
                  />
                </div>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Correo electrónico corporativo"
                  required
                  autoComplete="email"
                  className="w-full bg-inputBg border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primaryBlue outline-none"
                />

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="¿Qué desafío operativo quieres resolver?"
                  rows={3}
                  className="w-full bg-inputBg border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-primaryBlue outline-none resize-none"
                />

                <div className="flex items-start gap-3 py-2">
                  <input
                    id="accepted"
                    type="checkbox"
                    name="accepted"
                    checked={formData.accepted}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 accent-primaryBlue"
                    required
                  />
                  <label
                    htmlFor="accepted"
                    className="text-xs text-slate-500 leading-tight"
                  >
                    Acepto el tratamiento de datos personales de acuerdo a la
                    política de privacidad.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                    !canSubmit
                      ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-primaryBlue hover:bg-primaryBlueHover text-white shadow-lg shadow-primaryBlue/20"
                  }`}
                >
                  {loading ? "Procesando..." : "Enviar Solicitud"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* --- EL PROBLEMA --- */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 italic">
              "La operación necesita datos confiables, no suposiciones."
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Cuando la información se registra de forma manual o dispersa,
              aumentan los errores, se pierde trazabilidad y las decisiones se
              toman con datos incompletos o difíciles de verificar.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                t: "Información Fragmentada",
                d: "Datos que llegan tarde o incompletos al escritorio de la gerencia.",
              },
              {
                t: "Falta de Evidencias",
                d: "Dificultad para validar si los procesos se hicieron bajo el estándar.",
              },
              {
                t: "Decisiones Reactivas",
                d: "Resolver problemas cuando ya ocurrieron, en lugar de prevenirlos con datos reales.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="p-8 bg-white rounded-2xl border border-slate-200 shadow-sm"
              >
                <div className="w-12 h-12 bg-primaryBlue/10 text-primaryBlue rounded-full flex items-center justify-center mb-6 font-bold">
                  X
                </div>
                <h3 className="text-xl font-bold mb-3">{item.t}</h3>
                <p className="text-slate-500 leading-relaxed">{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- METODOLOGÍA --- */}
      <section id="ciclo" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-4">
            <div className="max-w-2xl">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Estandarización de Punta a Punta
              </h2>
              <p className="text-slate-600 text-lg">
                Nuestra plataforma cierra la brecha entre la planeación y la
                ejecución real.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                n: "01",
                t: "Diseño",
                d: "Configura y modela tus procesos con captura de tiempos, evidencia fotográfica, firmas y geolocalización.",
              },
              {
                n: "02",
                t: "Asignación",
                d: "Convierte procesos en tareas programadas para responsables específicos.",
              },
              {
                n: "03",
                t: "Ejecución",
                d: "El operario registra cada actividad desde la App móvil en tiempo real.",
              },
              {
                n: "04",
                t: "Análisis",
                d: "Dashboards en tiempo real con KPIs de cumplimiento.",
              },
            ].map((step, i) => (
              <div
                key={i}
                className="group relative p-8 bg-primaryDark rounded-3xl text-white transition-transform hover:-translate-y-2"
              >
                <span className="text-5xl font-black text-white/10 absolute top-4 right-6 group-hover:text-primaryBlue/20 transition-colors">
                  {step.n}
                </span>
                <h3 className="text-2xl font-bold mb-4 relative z-10">
                  {step.t}
                </h3>
                <p className="text-mutedText leading-relaxed relative z-10">
                  {step.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CARACTERÍSTICAS --- */}
      <section className="py-24 bg-primaryDark text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-8">Blindaje Operativo Total</h2>
              <div className="space-y-6">
                {[
                  {
                    i: "⏱️",
                    t: "Control de Tiempos Reales",
                    d: "Mide cuánto toma realmente cada actividad, no lo que dice el papel.",
                  },
                  {
                    i: "📍",
                    t: "Geolocalización GPS",
                    d: "Valida que el personal esté donde debe estar al ejecutar la tarea.",
                  },
                  {
                    i: "📸",
                    t: "Evidencia Multimedia",
                    d: "Fotos y firmas digitales obligatorias para cerrar cada proceso.",
                  },
                  { i: "📈", t: "Trazabilidad Completa", d: "Quién, cuándo, dónde y cómo." },
                ].map((feat, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-4 rounded-2xl hover:bg-white/5 transition-colors"
                  >
                    <span className="text-3xl">{feat.i}</span>
                    <div>
                      <h4 className="font-bold text-xl">{feat.t}</h4>
                      <p className="text-mutedText">{feat.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-primaryBlue rounded-3xl p-12 text-center space-y-6 shadow-2xl shadow-primaryBlue/20">
              <h3 className="text-3xl font-bold text-white italic">
                "De la operación reactiva al control estructurado y medible."
              </h3>
              <p className="text-white/80 font-medium">
                — PRODSYNC convierte procesos operativos en datos verificables en
                tiempo real.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- CTA FINAL --- */}
      <section className="py-24 bg-white text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">
            ¿Listo para transformar tu productividad?
          </h2>
          <p className="text-xl text-slate-500 mb-10">
            Da el siguiente paso hacia una operación más organizada, medible y
            estandarizada.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToForm}
              className="px-10 py-5 bg-primaryBlue hover:bg-primaryBlueHover text-white rounded-2xl font-bold text-xl transition-all shadow-xl shadow-primaryBlue/30"
            >
              Comenzar ahora
            </button>
            <a
              href="/login"
              className="px-10 py-5 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-2xl font-bold text-xl transition-all"
            >
              Acceso Clientes
            </a>
          </div>
        </div>
      </section>

      <footer className="py-12 border-t border-slate-100 text-center text-slate-400 text-sm">
        <p>© 2026 — PRODSYNC. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
